/**
 * SVG rendering component for word cloud
 * Handles word positioning, coloring, and click interactions
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { CLOUD_RADIUS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/config'

/**
 * Individual word element with animation
 */
const WordElement = React.memo(function WordElement({ word, fontFamily, fillColor, onClick, clickable, visible }) {
  return (
    <text
      data-times={word.times}
      data-text={word.text}
      textAnchor="middle"
      transform={`translate(${word.x}, ${word.y}) rotate(${word.rotate})`}
      style={{
        fontSize: `${Math.floor(word.size)}px`,
        fontFamily,
        fill: fillColor,
        // a word only offers the pointer where a click will do something, so the cloud never
        // promises a drill-down it has been drawn without
        cursor: clickable ? 'pointer' : 'default',
        opacity: visible ? 1 : 0,
        transition: 'opacity 200ms ease-in-out'
      }}
      onClick={onClick}
    >
      {word.text}
    </text>
  )
})

WordElement.propTypes = {
  word: PropTypes.object.isRequired,
  fontFamily: PropTypes.string.isRequired,
  fillColor: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
  visible: PropTypes.bool
}

/**
 * SVG container for word cloud
 */
const CloudSVG = React.forwardRef(
  ({ className, width, height, words, fontFamily, colorFn, background, onWordClick }, ref) => {
    const [visible, setVisible] = useState(false)
    const [adjusted, setAdjusted] = useState(false)

    const groupRef = useRef(null)

    // Calculate word colors
    const wordColors = useMemo(() => words.map((word, index) => colorFn(word.size, index)), [words, colorFn])

    // Handle word click
    const handleWordClick = useCallback(
      word => {
        if (onWordClick) {
          onWordClick({ text: word.text, word })
        }
      },
      [onWordClick]
    )

    // Adjust group position to center words
    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (!groupRef.current || words.length === 0) return

      // Small delay to ensure words are rendered
      const timer = setTimeout(() => {
        const rect = groupRef.current.getBBox()
        if (rect.width > 0 && rect.height > 0) {
          const translateX = width - (width / 2 + rect.x + rect.width / 2)
          const translateY = height - (height / 2 + rect.y + rect.height / 2)
          groupRef.current.setAttribute('transform', `translate(${translateX}, ${translateY})`)
          setAdjusted(true)
        }
      }, 50)

      // eslint-disable-next-line consistent-return
      return () => clearTimeout(timer)
    }, [words, width, height])

    // Fade in after adjustment
    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (adjusted) {
        const timer = setTimeout(() => setVisible(true), 100)
        return () => clearTimeout(timer)
      }
    }, [adjusted])

    // Reset visibility when words change
    useEffect(() => {
      setVisible(false)
      setAdjusted(false)
    }, [words])

    return (
      <svg ref={ref} className={className} width={width} height={height} style={{ display: 'block' }}>
        {/* Background */}
        <rect x={0} y={0} width={width} height={height} rx={CLOUD_RADIUS} ry={CLOUD_RADIUS} fill={background} />

        {/* Words group */}
        <g ref={groupRef} transform={`translate(${width / 2}, ${height / 2})`}>
          {words.map((word, index) => (
            <WordElement
              key={`${word.text}-${index}`}
              word={word}
              fontFamily={fontFamily}
              fillColor={wordColors[index]}
              visible={visible}
              clickable={Boolean(onWordClick)}
              onClick={() => handleWordClick(word)}
            />
          ))}
        </g>
      </svg>
    )
  }
)

CloudSVG.propTypes = {
  words: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
      rotate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      times: PropTypes.number,
      f: PropTypes.number
    })
  ).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  fontFamily: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
  colorFn: PropTypes.func.isRequired,
  onWordClick: PropTypes.func,
  className: PropTypes.string
}

CloudSVG.defaultProps = {
  className: 'word-cloud'
}

export default CloudSVG
